import React, { useContext, useEffect, useState } from 'react'
import {
  Button,
  Cell,
  DataBlock,
  Grid,
  IconButton,
  DangerButton,
  Row,
  Separator,
  useStatusOverlay,
  Toggle,
} from '@fs/zion-ui'
import { usePageView } from '@fs/zion-analytics'
import { css } from '@emotion/core'
import { getSessionId } from '@fs/zion-session'
import { ContentCopy } from '@fs/zion-icon'
import { copyToClipboard } from '@fs/zion-frontend-friends'
import { useTranslation } from 'react-i18next'
import { usePageTitle } from '../../../../app/PageTitleContext'
import { getBaseUrl } from '../../../../../services/toolsService'
import { DatsJobBaseContext } from '../DatsJobContext'
import { RatesAction } from '../../popups/RatesAction'
import { DmcExpander } from '../../../../common/DmcExpander'
import { DmcText } from '../../../../common/DmcText'
import { UserLink } from '../../../../app/Links'
import getUserSummary from '../../../../../services/userService'
import { CIS_USER_ID_CONTEXT } from '../../../../app/LookupContext'
import { utcToLocaleString } from '../../../../../util/DateUtil'
import ToolsButtons from '../ToolsButtons'
import { RawData } from '../../../../common/RawData'
import { PermissionsContext } from '../../../../app/PermissionsContext'

/* eslint-disable no-underscore-dangle */

export default () => {}

export function JobDetails() {
  usePageView({ page_detail: 'DMConsole:JobDetails' })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.tools.jobDetails'))

  const { datsJobState, datsJobActions } = useContext(DatsJobBaseContext)
  const sessionId = getSessionId()
  const jobData = datsJobState.jobData
  const [user, setUser] = useState(null)
  const [showRawData, setShowRawData] = useState(false)
  const { isRole } = useContext(PermissionsContext)
  useEffect(() => {
    datsJobActions.updateJobData()
    datsJobActions.clearRefreshObserver()
    datsJobActions.addRefreshObserver(datsJobActions.updateJobData)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!jobData) {
    return null
  }

  if (!user) {
    getUserSummary(jobData.userId, CIS_USER_ID_CONTEXT).then((userResult) => {
      setUser(userResult.data)
    })
  }

  const formattedJob = (
    <Grid>
      <Grid columns={5}>
        {/* Row */}
        <Cell columns={12}>
          <DataBlock
            inline
            heading={`${t('dats.tool', 'Tool')}:`}
            data={`${jobData.adminTool.groupName} -- ${jobData.adminTool.name}`}
          />

          <DataBlock
            inline
            heading={`${t('user.label', 'User')}:`}
            data={
              (user && <UserLink showId userId={user.dmcUser.cisId} userName={`${user.dmcUser.contactName}`} />) || ''
            }
          />
          <DataBlock
            inline
            heading={`${t('created', 'Created')}:`}
            data={utcToLocaleString(jobData._attributes.created, true)}
          />
          <DataBlock
            inline
            heading={`${t('modified.label', 'Modified')}:`}
            data={utcToLocaleString(jobData._attributes.modified, true)}
          />
          <DataBlock
            inline
            heading={`${t('dats.rea', 'Current Status')}:`}
            data={jobData.status ? t(`tools.jobStatus.${jobData.status}`, jobData.status) : ''}
          />
          <DataBlock
            inline
            heading={`${t('reason', 'Reason')}:`}
            data={
              jobData.finishedReason ? t(`tools.finishedReason.${jobData.finishedReason}`, jobData.finishedReason) : ''
            }
          />

          {jobData.statusChangeRequest && (
            <DataBlock
              inline
              heading={`${t('dats.statusChangeRequest', 'Status Change Request')}:`}
              data={jobData.statusChangeRequest}
            />
          )}
          <DataBlock
            inline
            heading={`${t('dats.targetRequestsPerSecond', 'Target Requests Per Second')}:`}
            data={jobData.requestsPerSecond}
          />
          <DataBlock
            inline
            heading={`${t('dats.actualRequestsPerSecond', 'Actual Requests Per Second')}:`}
            data={jobData.currentRequestsPerSecond}
          />
          <DataBlock
            inline
            heading={`${t('dats.itemsPerRequest', 'Items Per Request')}:`}
            data={jobData.itemsPerRequest}
          />
          <DataBlock inline heading={`${t('dats.workSize', 'Work Size')}:`} data={jobData.workSize} />
          <DataBlock inline heading={`${t('dats.workDone', 'Work Done')}:`} data={jobData.workDone} />
          <DataBlock inline heading={`${t('dats.Successful', 'Successful')}:`} data={jobData.successfulItemCount} />
          <DataBlock inline heading={`${t('dats.failed', 'Failed')}:`} data={jobData.failedItemCount} />
        </Cell>
      </Grid>
      <Grid columns={7}>
        {/* Row */}
        {jobData._links?.uploadInputFile && (
          <Cell columns={12}>
            <CurlCommands jobData={jobData} />
          </Cell>
        )}
      </Grid>
    </Grid>
  )

  const rawData = <RawData state={jobData} viewJson noExpander collapsed={false} />

  return (
    <>
      <Separator size="xxs" />
      <ToolsButtons datsJobActions={datsJobActions}>
        {(jobData.status !== 'WaitingForInputFile' ||
          jobData.status !== 'ParsingInputFile' ||
          jobData.status !== 'UploadingInputFile') && (
          <Button to={`${getBaseUrl()}${jobData._links?.inputFile?.href || ''}?sessionId=${sessionId}`} download>
            {t('dats.downloadInputFile', 'Download Input File')}
          </Button>
        )}
        {Object.keys(jobData._actions).map((actionKey) => {
          const action = jobData._actions[actionKey]
          let button = null
          if (!action.needsPayload) {
            button = (
              <DangerButton onClick={() => datsJobActions.actionOnClick(action, jobData._links)}>
                {t(`dats.${action.buttonText}`, `${action.buttonText}`)}
              </DangerButton>
            )
          }
          if (action.buttonText === 'Update Rates') {
            button = (
              <RatesAction
                buttonText={t(`dats.${action.buttonText}`, `${action.buttonText}`)}
                path={jobData._links[action.linkRel].href}
                method={action.httpMethod}
                datsJob={jobData}
                callback={datsJobActions.updateJobData}
              />
            )
          }
          return <div key={action.buttonText}>{button}</div>
        })}
        {isRole('Engineer Admin') && (
          <Toggle
            label={t('dats.rawData', 'Raw Data')}
            checked={showRawData}
            onChange={() => setShowRawData((prev) => !prev)}
          />
        )}
      </ToolsButtons>
      {showRawData ? rawData : formattedJob}
    </>
  )
}

function CurlCommands({ jobData }) {
  const { t } = useTranslation()
  const sessionId = getSessionId()

  return (
    <DmcExpander
      header={<DmcText text={t('dats.uploadWithCurl', 'Upload files to DATS with Curl')} />}
      body={
        <>
          Please try the new multipart upload feature. Use these commands only if the regular multipart upload fails.
          <CodeBlock
            heading={t('dats.uploadFile', 'Upload a File')}
            text={`curl --upload-file YOUR_FILE_PATH --request PUT "${jobData._links.uploadInputFile.href}"`}
          />
          <CodeBlock
            heading={t('dats.notifyDATSThatTheInputFileIsReady', 'Notify DATS that the Input File is ready')}
            text={`curl --data "{\\"statusChangeRequest\\": \\"InputFileReady\\"}" --header "User-Agent: security-bot-testing" --header "Content-Type: application/json" --header "Authorization: Bearer ${sessionId}" --request PUT "${getBaseUrl()}${
              jobData._links.self.href
            }"`}
          />
        </>
      }
    />
  )
}

const codeStyle = css`
  background-color: #eee;
  border-radius: 3px;
  font-family: courier, monospace;
  padding: 20px;
  word-wrap: break-word;
`

function CodeBlock({ text, heading }) {
  const { t } = useTranslation()
  const showStatusOverlay = useStatusOverlay()

  function onClickCopy() {
    copyToClipboard(text)
    showStatusOverlay({
      message: t('tools.copiedToClipboard', 'Copied to clipboard'),
      dismissable: true,
      type: 'normal',
    })
  }

  return (
    <Grid>
      {/* Row */}
      <Cell columns={10}>
        <Row>
          <DataBlock heading={heading} data="" />
          <IconButton onClick={() => onClickCopy()} Icon={ContentCopy} />
        </Row>
      </Cell>

      {/* Row */}
      <Cell columns={12}>
        <code css={codeStyle}>{text}</code>
      </Cell>
    </Grid>
  )
}

/* eslint-enable no-underscore-dangle */
